import type { OpenOptions, SignedLoginData } from "./types";

const DEFAULT_OPTIONS: Required<OpenOptions> = {
	mode: "popup",
	width: 800,
	height: 700,
};

/**
 * Open the feedback board in browser
 * Uses form submission to handle authentication with proper cookie handling
 */
export function openFeedbackBoard(
	signedData: SignedLoginData,
	options: OpenOptions = {},
): Window | null {
	const opts = { ...DEFAULT_OPTIONS, ...options };

	// Check if signed data has expired
	if (Date.now() > signedData.expiresAt) {
		console.error(
			"[Fdback] Signed login data has expired. Please generate new data.",
		);
		return null;
	}

	// Open popup/tab first (must be in direct response to user action)
	let targetWindow: Window | null = null;
	let targetName = "_blank";

	switch (opts.mode) {
		case "popup": {
			const left = Math.round((screen.width - opts.width) / 2);
			const top = Math.round((screen.height - opts.height) / 2);
			const features = `width=${opts.width},height=${opts.height},left=${left},top=${top},scrollbars=yes,resizable=yes`;
			targetName = "fdback_board";
			targetWindow = window.open("about:blank", targetName, features);
			break;
		}
		case "tab": {
			targetName = "_blank";
			targetWindow = window.open("about:blank", targetName);
			break;
		}
		case "redirect": {
			targetName = "_self";
			break;
		}
	}

	// Create and submit form to handle authentication
	// Form submission properly handles cookies and redirects across origins
	const form = document.createElement("form");
	form.method = "POST";
	form.action = signedData.url;
	form.target = targetName;
	form.style.display = "none";

	// Add headers as hidden fields (server needs to read from body)
	const addField = (name: string, value: string) => {
		const input = document.createElement("input");
		input.type = "hidden";
		input.name = name;
		input.value = value;
		form.appendChild(input);
	};

	// Add auth headers as body fields
	addField("_workspaceId", signedData.headers["x-workspace-id"]);
	addField("_timestamp", signedData.headers["x-timestamp"]);
	addField("_signature", signedData.headers["x-signature"]);

	// Add the original body fields
	const bodyData = JSON.parse(signedData.body);
	for (const [key, value] of Object.entries(bodyData)) {
		if (value !== undefined && value !== null) {
			addField(key, String(value));
		}
	}

	document.body.appendChild(form);
	form.submit();
	document.body.removeChild(form);

	return targetWindow;
}

/**
 * Check if code is running in a browser environment
 */
export function isBrowser(): boolean {
	return typeof window !== "undefined" && typeof document !== "undefined";
}
